import React, { useState, useEffect, useCallback } from 'react';

interface TetrisCell {
  filled: boolean;
  color: string;
}

type TetrisBoard = TetrisCell[][];

interface TetrisPiece {
  shape: number[][];
  color: string;
  x: number;
  y: number;
}

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;

const TETRIS_SHAPES = [
  { shape: [[1, 1, 1, 1]], color: '#00FFFF' }, // I
  { shape: [[1, 1], [1, 1]], color: '#FFFF00' }, // O
  { shape: [[0, 1, 0], [1, 1, 1]], color: '#800080' }, // T
  { shape: [[0, 1, 1], [1, 1, 0]], color: '#00FF00' }, // S
  { shape: [[1, 1, 0], [0, 1, 1]], color: '#FF0000' }, // Z
  { shape: [[1, 0, 0], [1, 1, 1]], color: '#0000FF' }, // J
  { shape: [[0, 0, 1], [1, 1, 1]], color: '#FFA500' }, // L
];

const Tetris: React.FC = () => {
  const [board, setBoard] = useState<TetrisBoard>(() =>
    Array(BOARD_HEIGHT).fill(null).map(() =>
      Array(BOARD_WIDTH).fill(null).map(() => ({ filled: false, color: '' }))
    )
  );
  const [currentPiece, setCurrentPiece] = useState<TetrisPiece | null>(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const createNewPiece = useCallback((): TetrisPiece => {
    const randomShape = TETRIS_SHAPES[Math.floor(Math.random() * TETRIS_SHAPES.length)];
    return {
      shape: randomShape.shape,
      color: randomShape.color,
      x: Math.floor(BOARD_WIDTH / 2) - Math.floor(randomShape.shape[0].length / 2),
      y: 0,
    };
  }, []);

  const isValidPosition = useCallback((piece: TetrisPiece, newX: number, newY: number, newShape?: number[][]): boolean => {
    const shape = newShape || piece.shape;
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          const boardX = newX + x;
          const boardY = newY + y;
          if (
            boardX < 0 ||
            boardX >= BOARD_WIDTH ||
            boardY >= BOARD_HEIGHT ||
            (boardY >= 0 && board[boardY][boardX].filled)
          ) {
            return false;
          }
        }
      }
    }
    return true;
  }, [board]);

  const rotatePiece = useCallback((piece: TetrisPiece): number[][] => {
    const rotated = piece.shape[0].map((_, index) =>
      piece.shape.map(row => row[index]).reverse()
    );
    return rotated;
  }, []);

  const placePiece = useCallback((piece: TetrisPiece) => {
    const newBoard = board.map(row => [...row]);
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x] && piece.y + y >= 0) {
          newBoard[piece.y + y][piece.x + x] = {
            filled: true,
            color: piece.color,
          };
        }
      }
    }
    setBoard(newBoard);
  }, [board]);

  const clearLines = useCallback(() => {
    let linesCleared = 0;
    const newBoard = board.filter(row => {
      const isFullLine = row.every(cell => cell.filled);
      if (isFullLine) linesCleared++;
      return !isFullLine;
    });

    while (newBoard.length < BOARD_HEIGHT) {
      newBoard.unshift(Array(BOARD_WIDTH).fill(null).map(() => ({ filled: false, color: '' })));
    }

    if (linesCleared > 0) {
      setBoard(newBoard);
      setLines(prev => prev + linesCleared);
      setScore(prev => prev + linesCleared * 100 * level);
      setLevel(() => Math.floor((lines + linesCleared) / 10) + 1);
    }
  }, [board, level, lines]);

  const movePiece = useCallback((dx: number, dy: number) => {
    if (!currentPiece || gameOver || isPaused) return;

    const newX = currentPiece.x + dx;
    const newY = currentPiece.y + dy;

    if (isValidPosition(currentPiece, newX, newY)) {
      setCurrentPiece(prev => prev ? { ...prev, x: newX, y: newY } : null);
    } else if (dy > 0) {
      // Piece can't move down, place it
      placePiece(currentPiece);
      clearLines();
      const newPiece = createNewPiece();
      if (isValidPosition(newPiece, newPiece.x, newPiece.y)) {
        setCurrentPiece(newPiece);
      } else {
        setGameOver(true);
      }
    }
  }, [currentPiece, gameOver, isPaused, isValidPosition, placePiece, clearLines, createNewPiece]);

  const rotatePieceHandler = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    const rotatedShape = rotatePiece(currentPiece);
    if (isValidPosition(currentPiece, currentPiece.x, currentPiece.y, rotatedShape)) {
      setCurrentPiece(prev => prev ? { ...prev, shape: rotatedShape } : null);
    }
  }, [currentPiece, gameOver, isPaused, rotatePiece, isValidPosition]);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft':
        movePiece(-1, 0);
        break;
      case 'ArrowRight':
        movePiece(1, 0);
        break;
      case 'ArrowDown':
        movePiece(0, 1);
        break;
      case 'ArrowUp':
        rotatePieceHandler();
        break;
      case ' ':
        setIsPaused(prev => !prev);
        break;
    }
  }, [movePiece, rotatePieceHandler]);

  const resetGame = () => {
    setBoard(Array(BOARD_HEIGHT).fill(null).map(() =>
      Array(BOARD_WIDTH).fill(null).map(() => ({ filled: false, color: '' }))
    ));
    setCurrentPiece(createNewPiece());
    setScore(0);
    setLines(0);
    setLevel(1);
    setGameOver(false);
    setIsPaused(false);
  };

  useEffect(() => {
    if (!currentPiece) {
      setCurrentPiece(createNewPiece());
    }
  }, [currentPiece, createNewPiece]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    if (gameOver || isPaused) return;

    const interval = setInterval(() => {
      movePiece(0, 1);
    }, Math.max(100, 1000 - (level - 1) * 100));

    return () => clearInterval(interval);
  }, [movePiece, level, gameOver, isPaused]);

  const renderBoard = () => {
    const displayBoard = board.map(row => [...row]);

    // Add current piece to display board
    if (currentPiece) {
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (currentPiece.shape[y][x] && currentPiece.y + y >= 0) {
            displayBoard[currentPiece.y + y][currentPiece.x + x] = {
              filled: true,
              color: currentPiece.color,
            };
          }
        }
      }
    }

    return displayBoard.map((row, y) =>
      row.map((cell, x) => (
        <div
          key={`${x}-${y}`}
          className="tetris-cell"
          style={{
            backgroundColor: cell.filled ? cell.color : '#f0f0f0',
            border: '1px solid #999',
          }}
        />
      ))
    );
  };

  return (
    <div className="tetris-game">
      <div className="tetris-header">
        <div className="tetris-stats">
          <div>Score: {score}</div>
          <div>Lines: {lines}</div>
          <div>Level: {level}</div>
        </div>
        <button onClick={resetGame} className="tetris-button">
          New Game
        </button>
      </div>
      
      <div className="tetris-main">
        <div className="tetris-board">
          {renderBoard()}
        </div>
        
        <div className="tetris-controls">
          <h3>Controls:</h3>
          <div>← → Move</div>
          <div>↓ Soft Drop</div>
          <div>↑ Rotate</div>
          <div>Space: Pause</div>
          
          {gameOver && (
            <div className="game-over">
              <h3>Game Over!</h3>
              <button onClick={resetGame} className="tetris-button">
                Play Again
              </button>
            </div>
          )}
          
          {isPaused && !gameOver && (
            <div className="paused">
              <h3>Paused</h3>
              <div>Press Space to continue</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tetris; 